import React, { useEffect, Fragment, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import { statusReasons } from 'constants';
import { BaseModal } from 'components/UI';
import { pickBy, identity } from 'lodash';
import { Formik, Form, Field } from 'formik';
import { logsActions } from 'state/modules/logs';
import { usersActions } from 'state/modules/users';
import Typography from '@material-ui/core/Typography';
import { UserStatusSelect } from 'components/AsyncFields';
import { MaterialSelectField, TextField } from 'components/FormFields';

const ChangeUserStatusModal = ({ id, open, status, handleClick, userType = 'CUSTOMER' }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const ChangeUserStatusSchema = Yup.object().shape({
    status: Yup.string().required('common.required_field'),
    status_reason: Yup.string().when('status', {
      is: (val) => ['SUSPENDED', 'BLOCKED'].includes(val),
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
    status_notes: Yup.string().when('status', {
      is: (val) => ['SUSPENDED', 'BLOCKED'].includes(val),
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
  });

  const { user, fetchingChangeUserStatus, fetchedChangeUserStatusSuccess } = useSelector(
    (state) => state.users
  );

  useEffect(() => {
    if (fetchedChangeUserStatusSuccess) {
      handleClick();
      dispatch(usersActions.resetChangeUserStatus());
    }
  }, [fetchedChangeUserStatusSuccess, handleClick, dispatch]);

  const onSubmit = async (values) => {
    // remove empty fields (status_reason and status_notes)
    const cleanedValues = pickBy(values, identity);
    const payload = { ...cleanedValues };
    const logsParams = {
      include_secondary: true,
      target_type: 'User',
      target_id: id,
      limit: 10,
      page: 1,
    };
    await dispatch(usersActions.fetchUpdateUserStatus(id, payload));
    dispatch(logsActions.fetchLogs(logsParams));
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  const statusDescription = user?.status_details
    ? statusReasons?.find(
        (status) => status.value === user?.status_details[user?.status_details.length - 1]
      )
    : {};

  return (
    <BaseModal
      open={open}
      maxWidth={440}
      hasEnterButton
      hasCancelButton
      onCancel={handleClick}
      onEnter={handleSubmit}
      enableCloseButton={false}
      loading={fetchingChangeUserStatus}
      title={i18n.t('users.change_status')}
      enterButtonLabel={i18n.t('users.update_user_type', {
        user_type: i18n.t(`users.types.${userType}`),
      })}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        onSubmit={onSubmit}
        validationSchema={ChangeUserStatusSchema}
        initialValues={{
          status: status,
          status_reason: statusDescription?.value || '',
          status_notes: user?.status_notes || '',
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Box mt={1}>
              <Field
                name="status"
                omitFields={['PENDING']}
                onChange={() => {
                  setFieldValue('status_reason', undefined);
                  setFieldValue('status_notes', undefined);
                }}
                component={UserStatusSelect}
                label={i18n.t('users.change_status')}
              />
            </Box>
            {['SUSPENDED', 'BLOCKED'].includes(values.status) && (
              <Fragment>
                <Box mt={1}>
                  <Field
                    name="status_reason"
                    options={statusReasons}
                    component={MaterialSelectField}
                    placeholder={i18n.t('users.reason')}
                  />
                </Box>
                <Box mt={1}>
                  <Field
                    fullWidth
                    type="text"
                    name="status_notes"
                    component={TextField}
                    label={i18n.t('users.reason_description')}
                  />
                </Box>
              </Fragment>
            )}
            <Box mt={2} fontSize={13}>
              <Typography variant="body2" gutterBottom>
                {i18n.t('users.change_status_msg', {
                  user_type: i18n.t(`users.types.${userType}`),
                })}
              </Typography>
            </Box>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

ChangeUserStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

ChangeUserStatusModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default ChangeUserStatusModal;
